import {
  HISTORY_MAX_RANGE_DAYS,
  addDays,
  compareDates,
  daysBetween,
  formatCalendarDate,
  parseIsoDate,
  ukDate,
  ukTime,
  type CalendarDate,
  type Location,
} from '@findmy/shared';

/** A range of whole UK days, both ends included. */
export interface DayRange {
  from: CalendarDate;
  to: CalendarDate;
}

export type Preset = 'today' | 'yesterday' | 'week' | 'month';

export const PRESETS: { id: Preset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: '30 days' },
];

export function presetRange(preset: Preset, now: number): DayRange {
  const today = ukDate(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { from: yesterday, to: yesterday };
    }
    case 'week':
      return { from: addDays(today, -6), to: today };
    case 'month':
      return { from: addDays(today, -29), to: today };
  }
}

export function sameRange(a: DayRange, b: DayRange): boolean {
  return compareDates(a.from, b.from) === 0 && compareDates(a.to, b.to) === 0;
}

/** The instants (UTC ms) the range covers: first day's midnight to the end of the last day. */
export function rangeInstants(range: DayRange): { from: number; to: number } {
  return { from: ukTime(range.from), to: ukTime(addDays(range.to, 1)) };
}

/**
 * The range in the URL (?from=2026-10-17&to=2026-10-24), or today. A hand-edited URL is
 * tidied up: the ends in order, nothing after today, and at most 31 days.
 */
export function rangeFromParams(fromText: string | null, toText: string | null, now: number): DayRange {
  const today = ukDate(now);
  let to = parseIsoDate(toText) ?? today;
  if (compareDates(to, today) > 0) to = today;
  let from = parseIsoDate(fromText) ?? to;
  if (compareDates(from, to) > 0) [from, to] = [to, from];
  if (daysBetween(from, to) >= HISTORY_MAX_RANGE_DAYS) from = addDays(to, -(HISTORY_MAX_RANGE_DAYS - 1));
  return { from, to };
}

/** "24/10/2026", or "17/10/2026 – 24/10/2026". */
export function rangeLabel(range: DayRange): string {
  return compareDates(range.from, range.to) === 0
    ? formatCalendarDate(range.from)
    : `${formatCalendarDate(range.from)} – ${formatCalendarDate(range.to)}`;
}

/** IP-based fixes can be miles out, so the route leaves them out unless asked. */
export const isApproximate = (location: Location) => location.source === 'ip';

/** The earliest day worth offering: history older than the retention period is gone. */
export function earliestDay(today: CalendarDate, retentionDays: number | undefined): CalendarDate {
  return addDays(today, -(retentionDays ?? 365));
}
